import { useCallback, useEffect, useRef, useState } from "react";
import type {
  FranklinContextPayload,
  FranklinHelpCenterHandle,
  FranklinTab,
} from "../types";

type FranklinStateEvent = CustomEvent<{
  isOpen?: boolean;
  target?: string;
  tab?: FranklinTab;
}>;

type FranklinTabEvent = CustomEvent<{
  tab?: FranklinTab;
}>;

export function useFranklin() {
  const franklinRef = useRef<FranklinHelpCenterHandle>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<FranklinTab>("ai");

  useEffect(() => {
    const handleStateChange = (event: Event) => {
      const detail = (event as FranklinStateEvent).detail;
      if (typeof detail?.isOpen === "boolean") {
        setIsOpen(detail.isOpen);
      }
      if (detail?.tab) {
        setActiveTab(detail.tab);
      }
    };

    const handleTabChange = (event: Event) => {
      const detail = (event as FranklinTabEvent).detail;
      if (detail?.tab) {
        setActiveTab(detail.tab);
      }
    };

    window.addEventListener("franklin:state-change", handleStateChange);
    window.addEventListener("franklin:tab-change", handleTabChange);

    return () => {
      window.removeEventListener("franklin:state-change", handleStateChange);
      window.removeEventListener("franklin:tab-change", handleTabChange);
    };
  }, []);

  const open = useCallback(() => {
    franklinRef.current?.open();
  }, []);

  const close = useCallback(() => {
    franklinRef.current?.close();
  }, []);

  const toggle = useCallback(() => {
    franklinRef.current?.toggle();
  }, []);

  const pushContext = useCallback((payload: FranklinContextPayload) => {
    franklinRef.current?.pushContext(payload);
  }, []);

  const setTab = useCallback((tab: FranklinTab) => {
    franklinRef.current?.setTab(tab);
  }, []);

  return {
    ref: franklinRef,
    open,
    close,
    toggle,
    pushContext,
    setTab,
    isOpen,
    activeTab,
  };
}
